import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight, AlertTriangle, CheckCircle, X, Plus, Search } from 'lucide-react';

const FieldMappingInterface = ({
  data,
  columnTypes,
  target,
  targetConfig,
  onMappingComplete,
  onBack
}) => {
  const [mappings, setMappings] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showUnmapped, setShowUnmapped] = useState(false);
  const [allTargetFields, setAllTargetFields] = useState([]);
  const mappingsRef = useRef({});
  const hasInitializedRef = useRef(false);

  // Update allTargetFields when targetConfig changes
  useEffect(() => {
    if (targetConfig && targetConfig.requiredFields && targetConfig.optionalFields) {
      const fields = [...targetConfig.requiredFields, ...targetConfig.optionalFields];
      console.log('FieldMappingInterface: Setting target fields', {
        requiredCount: targetConfig.requiredFields.length,
        optionalCount: targetConfig.optionalFields.length,
        totalFields: fields.length,
        sampleOptionalFields: targetConfig.optionalFields.slice(0, 10),
        isDynamic: targetConfig.isDynamic,
        linkFields: targetConfig.linkFields?.length || 0,
        factfindFields: targetConfig.factfindFields?.length || 0
      });
      setAllTargetFields(fields);
    } else {
      console.warn('FieldMappingInterface: targetConfig missing required/optional fields', targetConfig);
    }
  }, [targetConfig]);

  const initializeMappings = useCallback(() => {
    if (!data || !data.headers || allTargetFields.length === 0) return;

    // Preserve existing mappings only if we've already initialized (e.g., when allTargetFields changes)
    // This prevents losing user changes when dynamic config loads
    const existingMappings = hasInitializedRef.current ? { ...mappingsRef.current } : {};
    const initialMappings = { ...existingMappings };
    
    // Track which target fields have already been mapped to prevent duplicates
    const usedTargetFields = new Set(Object.values(existingMappings).filter(Boolean));

    // Try to auto-map based on field name similarity
    data.headers.forEach(header => {
      // Skip if this column already has a mapping
      if (initialMappings[header]) return;
      
      if (!header || !header.trim()) return;

      const cleanHeader = header.toLowerCase().trim();

      // Direct name matches
      const exactMatch = allTargetFields.find(field =>
        !usedTargetFields.has(field) && (
          field.toLowerCase() === cleanHeader ||
          field.toLowerCase().replace('_', ' ') === cleanHeader ||
          field.toLowerCase().replace(' ', '_') === cleanHeader
        )
      );

      if (exactMatch) {
        initialMappings[header] = exactMatch;
        usedTargetFields.add(exactMatch);
        return;
      }

      // Partial matches for common field names
      const partialMatches = {
        name: ['name', 'title', 'label'],
        email: ['email', 'mail', 'e-mail'],
        phone: ['phone', 'telephone', 'tel', 'mobile'],
        address: ['address', 'addr'],
        street: ['street', 'road', 'lane'],
        city: ['city', 'town'],
        postcode: ['postcode', 'postal', 'zip'],
        type: ['type', 'category', 'kind'],
        url: ['url', 'link', 'website'],
        description: ['description', 'desc', 'details', 'info'],
        order: ['order', 'sort', 'priority', 'sequence'],
        active: ['active', 'enabled', 'status']
      };

      for (const [field, keywords] of Object.entries(partialMatches)) {
        if (keywords.some(keyword => cleanHeader.includes(keyword))) {
          const targetField = allTargetFields.find(f => 
            !usedTargetFields.has(f) && f.toLowerCase().includes(field)
          );
          if (targetField) {
            initialMappings[header] = targetField;
            usedTargetFields.add(targetField);
            break;
          }
        }
      }
    });

    setMappings(initialMappings);
    mappingsRef.current = initialMappings;
    hasInitializedRef.current = true;
  }, [data, allTargetFields]);

  useEffect(() => {
    initializeMappings();
  }, [initializeMappings]);

  const validateMappings = useCallback(() => {
    const errors = {};

    // Check that all required fields are mapped
    targetConfig.requiredFields.forEach(field => {
      const isMapped = Object.values(mappings).includes(field);
      if (!isMapped) {
        errors[field] = `Required field "${field}" must be mapped to a column`;
      }
    });

    // Check for duplicate mappings
    const usedFields = {};
    Object.entries(mappings).forEach(([column, field]) => {
      if (field && usedFields[field]) {
        errors[field] = `Field "${field}" is mapped to multiple columns`;
      }
      usedFields[field] = true;
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [mappings, targetConfig.requiredFields]);

  useEffect(() => {
    validateMappings();
  }, [validateMappings]);

  const handleMappingChange = (column, targetField) => {
    setMappings(prev => ({
      ...prev,
      [column]: targetField
    }));
  };

  const removeMappingForField = (targetField) => {
    setMappings(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(column => {
        if (updated[column] === targetField) {
          delete updated[column];
        }
      });
      mappingsRef.current = updated;
      return updated;
    });
  };

  const handleContinue = () => {
    if (validateMappings()) {
      // Filter out empty mappings
      const finalMappings = Object.fromEntries(
        Object.entries(mappings).filter(([, targetField]) => targetField)
      );
      onMappingComplete(finalMappings);
    }
  };

  const getMappedColumns = () => {
    return data.headers.filter(header => mappings[header]);
  };

  const getUnmappedColumns = () => {
    return data.headers.filter(header => !mappings[header]);
  };

  const getUnmappedTargetFields = () => {
    const mappedFields = Object.values(mappings).filter(Boolean);
    return allTargetFields.filter(field => !mappedFields.includes(field));
  };

  const filteredHeaders = data.headers.filter(header => {
    if (!header) return false;

    const matchesSearch = !searchTerm ||
      header.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = !showUnmapped || !mappings[header];

    return matchesSearch && matchesFilter;
  });

  const renderColumnCard = (header, index) => {
    const type = columnTypes[header];
    const currentMapping = mappings[header];
    const sampleValues = data.rows.slice(0, 3).map(row => row[index]).filter(v => v);

    return (
      <div key={header} className="border border-gray-200 rounded-lg p-4 bg-white">
        <div className="mb-3">
          <h4 className="font-medium text-gray-900 mb-1">{header}</h4>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span className="px-2 py-1 bg-gray-100 rounded text-xs">{type}</span>
            <span>•</span>
            <span>{data.totalRows} rows</span>
          </div>
        </div>

        {sampleValues.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-gray-600 mb-1">Sample values:</p>
            <div className="text-xs text-gray-500 space-y-0.5">
              {sampleValues.map((value, idx) => (
                <div key={idx} className="truncate">"{String(value)}"</div>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Map to target field:
          </label>
          <select
            value={currentMapping || ''}
            onChange={(e) => handleMappingChange(header, e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">-- Don't map --</option>
            {allTargetFields.map(field => (
              <option key={field} value={field}>
                {field}
                {targetConfig.requiredFields.includes(field) ? ' *' : ''}
              </option>
            ))}
          </select>
        </div>

        {currentMapping && (
          <div className="mt-2 flex items-center gap-2 text-sm">
            <ArrowRight size={16} className="text-green-600" />
            <span className="text-green-700 font-medium">{currentMapping}</span>
            {targetConfig.requiredFields.includes(currentMapping) && (
              <span className="text-red-500">*</span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTargetField = (field) => {
    const isRequired = targetConfig.requiredFields.includes(field);
    const isMapped = Object.values(mappings).includes(field);
    const hasError = validationErrors[field];
    const mappedColumn = Object.keys(mappings).find(col => mappings[col] === field);

    return (
      <div
        key={field}
        className={`border rounded-lg p-3 ${
          hasError ? 'border-red-300 bg-red-50' :
          isMapped ? 'border-green-300 bg-green-50' :
          isRequired ? 'border-yellow-300 bg-yellow-50' :
          'border-gray-200 bg-gray-50'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-medium">{field}</span>
            {isRequired && <span className="text-red-500 text-sm">*</span>}
          </div>
          {isMapped && (
            <button
              onClick={() => removeMappingForField(field)}
              className="text-gray-500 hover:text-red-500"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {isMapped && mappedColumn && (
          <div className="mt-1 text-sm text-green-700">
            ← Mapped from "{mappedColumn}"
          </div>
        )}

        {hasError && (
          <div className="mt-1 text-sm text-red-600">
            {hasError}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold mb-2">Field Mapping</h3>
        <p className="text-gray-600">
          Map your Excel columns to the target database fields. Required fields are marked with *.
        </p>
      </div>

      {/* Summary Panel */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="text-blue-600" size={20} />
            <h4 className="font-medium text-blue-800">Mapped Columns</h4>
          </div>
          <p className="text-2xl font-bold text-blue-700">{getMappedColumns().length}</p>
          <p className="text-sm text-blue-600">of {data.headers.length} columns</p>
        </div>

        <div className={`border rounded-lg p-4 ${
          Object.keys(validationErrors).length > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
        }`}>
          <div className="flex items-center gap-2 mb-2">
            {Object.keys(validationErrors).length > 0 ? (
              <AlertTriangle className="text-red-600" size={20} />
            ) : (
              <CheckCircle className="text-green-600" size={20} />
            )}
            <h4 className={`font-medium ${
              Object.keys(validationErrors).length > 0 ? 'text-red-800' : 'text-green-800'
            }`}>
              Required Fields
            </h4>
          </div>
          <p className={`text-2xl font-bold ${
            Object.keys(validationErrors).length > 0 ? 'text-red-700' : 'text-green-700'
          }`}>
            {targetConfig.requiredFields.length - Object.keys(validationErrors).length}
          </p>
          <p className={`text-sm ${
            Object.keys(validationErrors).length > 0 ? 'text-red-600' : 'text-green-600'
          }`}>
            of {targetConfig.requiredFields.length} mapped
          </p>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Plus className="text-gray-600" size={20} />
            <h4 className="font-medium text-gray-800">Unmapped Columns</h4>
          </div>
          <p className="text-2xl font-bold text-gray-700">{getUnmappedColumns().length}</p>
          <p className="text-sm text-gray-600">will be ignored</p>
        </div>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search columns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={showUnmapped}
            onChange={(e) => setShowUnmapped(e.target.checked)}
            className="rounded"
          />
          <span className="text-sm">Show unmapped only</span>
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Source Columns */}
        <div>
          <h4 className="text-lg font-medium mb-4">Source Columns ({filteredHeaders.length})</h4>
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {filteredHeaders.map((header, index) =>
              renderColumnCard(header, data.headers.indexOf(header))
            )}
          </div>
        </div>

        {/* Target Fields */}
        <div>
          <h4 className="text-lg font-medium mb-4">Target Fields</h4>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            <div className="space-y-2">
              <h5 className="font-medium text-red-700">Required Fields</h5>
              {targetConfig.requiredFields.map(renderTargetField)}
            </div>
            <div className="space-y-2">
              <h5 className="font-medium text-gray-700">Optional Fields</h5>
              {targetConfig.optionalFields.map(renderTargetField)}
            </div>
          </div>
        </div>
      </div>

      {/* Validation Errors */}
      {Object.keys(validationErrors).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="text-red-600" size={20} />
            <h4 className="font-medium text-red-800">Mapping Issues</h4>
          </div>
          <ul className="text-sm text-red-700 space-y-1">
            {Object.values(validationErrors).map((error, index) => (
              <li key={index}>• {error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between pt-6 border-t">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <ChevronLeft size={16} />
          Back to Type Detection
        </button>
        <button
          onClick={handleContinue}
          disabled={Object.keys(validationErrors).length > 0}
          className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Continue to Preview
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default FieldMappingInterface;